(ns web3.components.text
  (:require
    [web3.view :as v]
    [reagent.core :as r]
    [web3.utils :as utils]
    [web3.components.progress :as progress]
    [web3.view.hiccup :as hiccup]))

(defn resize! [^js element]
  (-> element .-style .-height (set! "auto"))
  (-> element .-style .-height (set! (str (.-scrollHeight element) "px"))))

(defn resize-input [component]
  (when (:multi-line? (:props component))
    (some-> (:element @(:state component))
            (resize!))))

(v/defview render-helptext
  {:component-will-receive-props v/set-prev-props!}
  [{:keys [text
           persistent?
           pad?
           dense?
           focused?
           ephemeral-helptext]}]

  ;; 'ephemeral-helptext' will temporarily
  ;; replace whatever other helptext is provided,
  ;; with fade-in-out transitions.
  (let [text-content (when (and (not ephemeral-helptext)
                                (or focused? persistent?)
                                (not (empty? text)))
                       [:div.margin-v-1.text-black text])
        padding (when (and (not text-content)
                           (or persistent? pad?))
                  [:div.margin-v-1 {:key "blank-helptext"} "\u00A0"])]
    [:div.mdc-textfield-helptext.relative
     {:aria-hidden true
      :key "label"
      :class (when persistent? "mdc-textfield-helptext--persistent")}
     text-content
     [:div.absolute.top-0.left-0.text-blue
      {:style {:opacity (if ephemeral-helptext 1 0)
               :transition "opacity 0.4s ease"}}
      (or ephemeral-helptext (:ephemeral-helptext (:props (v/prev))))]
     padding]))

(v/defview input
  {:component-did-update resize-input
   :component-did-mount resize-input}
  [{:keys [id

           label

           ;; displayed to the left of the icon
           label-icon

           ;; placeholder disappears when content is typed
           placeholder

           ;; placeholder-right disappears when focused
           placeholder-right

           default-value
           value

           ;; suggested value is _only_ used to compare to current value,
           ;; & if suggested value == current value when field is clicked,
           ;; the input contents are selected.
           suggested-value

           ;; displayed under the textfield.
           ;; normally, displayed only when 'dirty'.
           helptext

           ;; when true, placeholder is always visible, and space is
           ;; reserved for one line of text.
           helptext-persistent?
           helptext-pad?

           ;; when true, no space is reserved for `helptext` when empty.
           dense?

           ;; suitable for displaying content, even outside of a form.
           read-only?

           ;; will render loading bar
           loading?

           ;; will render styled as error
           error?
           invalid?

           on-focus
           on-blur

           ;; view-as should be a function, which will wrap content
           ;; when in `read-only?` mode.
           view-as

           ;; textarea, minimum 2 lines, auto-adjusts height
           multi-line?

           ;; content to be displayed to the right of the textfield.
           ;; (the bottom-line will extend under this content.)
           ;; when `multi-line?` is true, always displayed at top right.
           content/inline-right
           content/inline-right-always?

           ;; props to be applied to the outermost container element
           container
           ;; props to be applied to mdc textfield element
           mdc-container

           on-change
           ephemeral-helptext

           encoding]
    :as props}]
  (let [{:keys [state]} (r/current-component)
        {:keys [encode decode]} (if (map? encoding)
                                  encoding
                                  (get {:link-data {:encode utils/link-data-encode
                                                    :decode utils/link-data-decode}} encoding))
        {:keys [focused?]} @state
        invalid? (or error? invalid?)
        current-value (cond-> (or value default-value)
                              decode (some->
                                      (decode)))

        value? (utils/some-str current-value)

        label-floating? (or value?
                            focused?)

        ;; merge props
        props (-> {:type "text"
                   :class (str "mdc-textfield__input font-proba flex-auto z-2 "
                               (when read-only? " text-dark-gray")
                               (when multi-line? " flex-none "))
                   :style (cond-> {}
                                  multi-line? (assoc :resize "vertical")
                                  read-only? (assoc :resize "none"))
                   :ref #(when % (swap! state assoc :element %))
                   :disabled (when read-only? "disabled")
                   :on-focus (fn [e]
                               (when-let [input (:element @state)]
                                 (set! (.. input -style -height) "auto")
                                 (when (and suggested-value
                                            (= suggested-value current-value))
                                   (doto input
                                     (aset "selectionStart" 0)
                                     (aset "selectionEnd" (count current-value)))))
                               (swap! state assoc :focused? true)
                               (when on-focus (on-focus e)))
                   :on-blur (fn [e]
                              (when-let [input (:element @state)]
                                (set! (.. input -style -height) ""))
                              (swap! state assoc :focused? false)
                              (when on-blur (on-blur e)))
                   :on-change (when on-change
                                (fn [^js e]
                                  ;; NOTE
                                  ;; if :encode is set, :on-change events _do not_ get called
                                  ;; with the event, but rather an object containing (.. -target -value).
                                  ;; (if we mutate the event itself, we screw up react's input handling)
                                  (on-change (if encode
                                               #js {:target #js {:value (some-> (.. e -target -value)
                                                                                (encode))}}
                                               e) e)))}

                  (v/merge-props (-> props
                                     ;; decode values
                                     (cond-> decode
                                             (-> (update :value #(cond-> % % decode))
                                                 (update :default-value #(cond-> % % decode))))
                                     (dissoc :on-change
                                             :on-focus
                                             :on-save
                                             :on-blur
                                             :loading?
                                             :read-only?
                                             :label
                                             :container
                                             :helptext
                                             :helptext-persistent?
                                             :helptext-pad?
                                             :placeholder-right
                                             :error?
                                             :invalid?
                                             :dirty?
                                             :content/inline-right
                                             :content/inline-right-always?
                                             :view-as
                                             :multi-line?
                                             :dense?
                                             :label-icon
                                             :mdc-container
                                             :ephemeral-helptext
                                             :suggested-value))))]
    [:div
     (v/merge-props container
                    {:key id
                     :style (when (and read-only? (not value?))
                              {:display "none"})})
     [:div.mdc-textfield.mdc-textfield--upgraded.width-100p
      (v/merge-props mdc-container
                     {:key "textfield"
                      :class (str (if read-only? " mdc-textfield--readonly"
                                                 " mdc-textfield--editable")
                                  (when invalid? " mdc-textfield--invalid")
                                  (when focused? " mdc-textfield--focused")
                                  (if multi-line? " mdc-textfield--textarea"
                                                  " mdc-textfield--single-line")
                                  (if label-floating? " mdc-textfield--label-float-above"
                                                      " mdc-textfield--label-placeholder"))})
      [:div.flex.items-center.width-100p.relative

       (if (and read-only? view-as)
         [:div.flex-auto.z-2.pad-v-2.pre-wrap (view-as (cond-> (or default-value value)
                                                               decode (some-> (decode))))]
         [(if multi-line? :textarea :input)
          props])
       (some->> (when (and inline-right (or (not read-only?)
                                            inline-right-always?)) inline-right)
                (conj (if multi-line? [:div.absolute.top-0.right-0.margin-top-n3] [:div.pad-left-2])))
       (when (and (not focused?) (not value?) placeholder-right)
         (->> [:div.absolute.height-100p.right-0.flex.items-center.pad-top-1 placeholder-right]
              (conj (if multi-line? [:div.absolute.top-0.right-0.margin-top-n2]
                                    [:div.relative.width-0.height-100p]))))]
      [:label.mdc-textfield__label.z-2
       {:for id
        :key "label"
        :class (str "flex items-center "
                    (when (or value? focused?) "mdc-textfield__label--float-above")
                    (if read-only?
                      " text-black"
                      " text-dark-gray"))}
       (some-> label-icon
               (hiccup/size 12)
               (hiccup/class "margin-right-2"))
       label]
      (if loading?
        [progress/indeterminate {:height 2
                                 :style {:margin-bottom -1}
                                 :key "progress"
                                 :bg-color "#e2e2e2"
                                 :class "absolute left-0 right-0 bottom-0 z-9"}]
        [:div.mdc-textfield__bottom-line {:key "bottom-bar"
                                          :class (when focused? "mdc-textfield__bottom-line--active")}])]

     [render-helptext {:text helptext
                       :persistent? (or helptext-persistent? invalid?)
                       :pad? helptext-pad?
                       :focused? focused?
                       :dense? dense?
                       :ephemeral-helptext ephemeral-helptext}]]))

(defn single-line-with-label [{:keys [label] :as props} text]
  [:div.pad-all-3
   (dissoc props :label)
   [:div {:key :label} label]
   [:div.text-dark-gray.text-size-14 text]])